import type {
  TryOnJob,
  TryOnJobResponse,
  TryOnJobsResponse,
  TryOnReference,
  TryOnReferencesResponse,
  TryOnSelection,
} from '../../shared/tryOn';
import { readError } from './api';

async function request<T>(url: string, init?: RequestInit): Promise<T> {
  const response = await fetch(url, {
    credentials: 'include',
    headers: {
      Accept: 'application/json',
      ...(init?.body && !(init.body instanceof FormData)
        ? { 'Content-Type': 'application/json' }
        : {}),
      ...init?.headers,
    },
    ...init,
  });
  if (!response.ok) throw new Error(await readError(response));
  return (await response.json()) as T;
}

export async function listTryOnReferences(): Promise<TryOnReference[]> {
  return (await request<TryOnReferencesResponse>('/api/try-on/references')).references;
}

export async function createTryOnReference(
  name: string,
  photo: File,
  consent: boolean,
): Promise<TryOnReference> {
  const preparedPhoto = await preparePrivateReference(photo);
  const body = new FormData();
  body.set('name', name);
  body.set('photo', preparedPhoto);
  body.set('consent', String(consent));
  return (
    await request<{ reference: TryOnReference }>('/api/try-on/references', {
      method: 'POST',
      body,
    })
  ).reference;
}

async function preparePrivateReference(photo: File): Promise<File> {
  if (typeof createImageBitmap !== 'function' || typeof document === 'undefined') {
    throw new Error('This browser cannot safely prepare the reference photo.');
  }
  try {
    const image = await createImageBitmap(photo);
    const maximum = 2048;
    const ratio = Math.min(1, maximum / Math.max(image.width, image.height));
    const canvas = document.createElement('canvas');
    canvas.width = Math.max(1, Math.round(image.width * ratio));
    canvas.height = Math.max(1, Math.round(image.height * ratio));
    const context = canvas.getContext('2d');
    if (!context) throw new Error('Canvas is unavailable.');
    context.fillStyle = '#ffffff';
    context.fillRect(0, 0, canvas.width, canvas.height);
    context.drawImage(image, 0, 0, canvas.width, canvas.height);
    image.close();
    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', 0.9),
    );
    if (!blob) throw new Error('Photo conversion failed.');
    return new File([blob], 'try-on-reference.jpg', {
      type: 'image/jpeg',
      lastModified: Date.now(),
    });
  } catch {
    throw new Error(
      'The photo could not be prepared privately. Try a different JPEG, PNG, or WebP image.',
    );
  }
}

export async function deleteTryOnReference(id: string): Promise<void> {
  await request(`/api/try-on/references/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

export async function listTryOnJobs(): Promise<TryOnJob[]> {
  return (await request<TryOnJobsResponse>('/api/try-on/jobs')).jobs;
}

export async function createTryOnJob(
  referenceId: string,
  selection: TryOnSelection,
  requestKey: string,
): Promise<TryOnJob> {
  return (
    await request<TryOnJobResponse>('/api/try-on/jobs', {
      method: 'POST',
      headers: { 'X-Try-On-Key': requestKey },
      body: JSON.stringify({ referenceId, selection, consent: true }),
    })
  ).job;
}

export async function getTryOnJob(id: string): Promise<TryOnJob> {
  return (await request<TryOnJobResponse>(`/api/try-on/jobs/${encodeURIComponent(id)}`)).job;
}

export async function cancelTryOnJob(id: string): Promise<TryOnJob> {
  return (
    await request<TryOnJobResponse>(`/api/try-on/jobs/${encodeURIComponent(id)}`, {
      method: 'PATCH',
      body: JSON.stringify({ action: 'cancel' }),
    })
  ).job;
}

export async function retryTryOnJob(id: string): Promise<TryOnJob> {
  return (
    await request<TryOnJobResponse>(`/api/try-on/jobs/${encodeURIComponent(id)}`, {
      method: 'POST',
      body: JSON.stringify({ action: 'retry' }),
    })
  ).job;
}

export async function deleteTryOnJob(id: string): Promise<void> {
  await request(`/api/try-on/jobs/${encodeURIComponent(id)}`, { method: 'DELETE' });
}
